import React, { createContext, useContext, useState, useCallback } from "react";

const NavigationContext = createContext();

/**
 * NavigationProvider - Manages progressive disclosure state for the sidenav.
 *
 * Tiers:
 * - Tier 0: No branch selected → Branch Homepage, Settings
 * - Tier 1: Branch selected → + Employees, Customers
 * - Tier 2: Customer selected → + Microgrid
 * - Tier 3: Microgrid selected → + Devices, Simulations, Data
 */
export const NavigationProvider = ({ children }) => {
  const [selectedBranchId, setSelectedBranchIdState] = useState(() =>
    sessionStorage.getItem("selectedBranchId") || null
  );
  const [selectedBranchName, setSelectedBranchNameState] = useState(() =>
    sessionStorage.getItem("selectedBranchName") || null
  );
  const [selectedCustomerEmail, setSelectedCustomerEmailState] = useState(() =>
    sessionStorage.getItem("selectedCustomerEmail") || null
  );
  const [selectedCustomerName, setSelectedCustomerNameState] = useState(() =>
    sessionStorage.getItem("customerFirstName") || null
  );
  const [selectedMicrogrid, setSelectedMicrogridState] = useState(() =>
    sessionStorage.getItem("microgridDesign") || null
  );
  const [selectedSimulation, setSelectedSimulationState] = useState(() => {
    const stored = sessionStorage.getItem("selectedSimulation");
    return stored ? JSON.parse(stored) : null;
  });
  const [storageAccountName, setStorageAccountNameState] = useState(() =>
    sessionStorage.getItem("storageAccountName") || null
  );
  const [timeSeriesFileName, setTimeSeriesFileNameState] = useState(() => {
    const stored = sessionStorage.getItem("timeSeriesFileName");
    return stored && stored !== "0" ? stored : null;
  });

  /**
   * Set the selected branch. Clears all downstream state (customer, microgrid,
   * simulation, etc.) since a new branch means starting fresh.
   * @param {Object|null} branch - { id, branch_name }
   */
  const setSelectedBranch = useCallback((branch) => {
    if (branch) {
      const id = String(branch.id);
      const name = branch.branch_name ?? null;

      setSelectedBranchIdState(id);
      setSelectedBranchNameState(name);

      sessionStorage.setItem("selectedBranchId", id);
      if (name) sessionStorage.setItem("selectedBranchName", name); else sessionStorage.removeItem("selectedBranchName");
    } else {
      setSelectedBranchIdState(null);
      setSelectedBranchNameState(null);

      sessionStorage.removeItem("selectedBranchId");
      sessionStorage.removeItem("selectedBranchName");
    }

    setSelectedCustomerEmailState(null);
    setSelectedCustomerNameState(null);
    setSelectedMicrogridState(null);
    setSelectedSimulationState(null);
    setStorageAccountNameState(null);
    setTimeSeriesFileNameState(null);

    sessionStorage.removeItem("selectedCustomerEmail");
    sessionStorage.removeItem("customerFirstName");
    sessionStorage.removeItem("microgridDesign");
    sessionStorage.removeItem("selectedSimulation");
    sessionStorage.removeItem("storageAccountName");
    sessionStorage.removeItem("timeSeriesFileName");
  }, []);

  /**
   * Set the selected customer. Clears downstream microgrid/simulation state.
   */
  const setSelectedCustomer = useCallback((email, firstName) => {
    setSelectedCustomerEmailState(email);
    setSelectedCustomerNameState(firstName);
    setSelectedMicrogridState(null);
    setSelectedSimulationState(null);
    setTimeSeriesFileNameState(null);

    if (email) {
      sessionStorage.setItem("selectedCustomerEmail", email);
    } else {
      sessionStorage.removeItem("selectedCustomerEmail");
    }
    if (firstName) {
      sessionStorage.setItem("customerFirstName", firstName);
    } else {
      sessionStorage.removeItem("customerFirstName");
    }
    sessionStorage.removeItem("microgridDesign");
    sessionStorage.removeItem("selectedSimulation");
    sessionStorage.removeItem("timeSeriesFileName");
  }, []);

  /**
   * Set the selected microgrid. Clears downstream simulation state.
   */
  const setSelectedMicrogrid = useCallback((name) => {
    setSelectedMicrogridState(name);
    setSelectedSimulationState(null);
    setTimeSeriesFileNameState(null);

    if (name) {
      sessionStorage.setItem("microgridDesign", name);
    } else {
      sessionStorage.removeItem("microgridDesign");
    }
    sessionStorage.removeItem("selectedSimulation");
    sessionStorage.removeItem("timeSeriesFileName");
  }, []);

  /**
   * Set the selected simulation.
   * @param {Object|null} simulation - { name, startDate, endDate } or null to clear
   */
  const setSelectedSimulation = useCallback((simulation) => {
    setSelectedSimulationState(simulation);
    if (simulation) {
      sessionStorage.setItem("selectedSimulation", JSON.stringify(simulation));
    } else {
      sessionStorage.removeItem("selectedSimulation");
    }
  }, []);

  const setStorageAccountName = useCallback((name) => {
    setStorageAccountNameState(name);
    if (name) {
      sessionStorage.setItem("storageAccountName", name);
    } else {
      sessionStorage.removeItem("storageAccountName");
    }
  }, []);

  const setTimeSeriesFileName = useCallback((fileName) => {
    setTimeSeriesFileNameState(fileName);
    if (fileName) {
      sessionStorage.setItem("timeSeriesFileName", fileName);
    } else {
      sessionStorage.removeItem("timeSeriesFileName");
    }
  }, []);

  /**
   * Clear all navigation state (used on logout).
   */
  const clearNavigation = useCallback(() => {
    setSelectedBranchIdState(null);
    setSelectedBranchNameState(null);
    setSelectedCustomerEmailState(null);
    setSelectedCustomerNameState(null);
    setSelectedMicrogridState(null);
    setSelectedSimulationState(null);
    setStorageAccountNameState(null);
    setTimeSeriesFileNameState(null);

    sessionStorage.removeItem("selectedBranchId");
    sessionStorage.removeItem("selectedBranchName");
    sessionStorage.removeItem("selectedCustomerEmail");
    sessionStorage.removeItem("customerFirstName");
    sessionStorage.removeItem("microgridDesign");
    sessionStorage.removeItem("selectedSimulation");
    sessionStorage.removeItem("storageAccountName");
    sessionStorage.removeItem("timeSeriesFileName");
  }, []);

  const navigationTier = selectedMicrogrid
    ? 3
    : selectedCustomerEmail
      ? 2
      : selectedBranchId
        ? 1
        : 0;

  return (
    <NavigationContext.Provider
      value={{
        // Branch state
        selectedBranchId,
        selectedBranchName,

        // Customer state
        selectedCustomerEmail,
        selectedCustomerName,

        // Microgrid / simulation state
        selectedMicrogrid,
        selectedSimulation,
        storageAccountName,
        timeSeriesFileName,

        navigationTier,

        // Actions
        setSelectedBranch,
        setSelectedCustomer,
        setSelectedMicrogrid,
        setSelectedSimulation,
        setStorageAccountName,
        setTimeSeriesFileName,
        clearNavigation,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Hook to access navigation context.
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};

export default NavigationContext;
